import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import hospitalsData from '@data/hospitals/hospitals.json';

const HospitalDirectory = () => {
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [filterCity, setFilterCity] = useState<string>('All');
  const [filterSpecialty, setFilterSpecialty] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    setHospitals(hospitalsData.hospitals);
  }, []);

  const cities = ['All', 'Beijing', 'Shanghai', 'Guangzhou', 'Other'];
  const specialties = [
    'All',
    'Oncology',
    'Cardiology',
    'Neurology',
    'Orthopedics',
    'Gastroenterology',
    'Endocrinology',
    'Rheumatology',
    'Respiratory Medicine'
  ];

  const filteredHospitals = hospitals.filter((hospital) => {
    const matchesCity = filterCity === 'All' || hospital.city === filterCity;
    const matchesSpecialty = filterSpecialty === 'All' ||
      hospital.specialties.includes(filterSpecialty);
    const matchesSearch = searchTerm === '' ||
      hospital.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hospital.specialties.some((s: string) =>
        s.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return matchesCity && matchesSpecialty && matchesSearch;
  });

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Hospital Directory
        </h1>
        <p className="text-gray-600">
          Explore China's top-tier hospitals with international departments and world-class facilities.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search hospitals or specialties..."
              className="input-field"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <select
              className="input-field"
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
            >
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specialty
            </label>
            <select
              className="input-field"
              value={filterSpecialty}
              onChange={(e) => setFilterSpecialty(e.target.value)}
            >
              {specialties.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {specialty}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredHospitals.map((hospital) => (
          <Link
            key={hospital.id}
            to={`/hospitals/${hospital.id}`}
            className="card hover:shadow-lg transition-shadow cursor-pointer"
          >
          <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center mb-4">
              {hospital.images && hospital.images.icon ? (
                <img src={hospital.images.icon} alt={hospital.name} className="w-16 h-16" />
              ) : (
                <svg className="w-16 h-16 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2 2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-4 4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
            </div>

            <div className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-xl font-semibold text-gray-900 flex-grow">
                  {hospital.name}
                </h2>
                {hospital.certifications.includes('JCI') && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    JCI
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-600 mb-3">{hospital.nameCn}</p>

              <div className="flex items-center text-sm text-gray-600 mb-3">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {hospital.city}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {hospital.specialties.slice(0, 3).map((specialty: string) => (
                  <span
                    key={specialty}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {specialty}
                  </span>
                ))}
                {hospital.specialties.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{hospital.specialties.length - 3} more
                  </span>
                )}
              </div>

              {hospital.hasInternationalDepartment && (
                <div className="flex items-center text-sm text-primary-600">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  International Department Available
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      {filteredHospitals.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hospitals found
          </h3>
          <p className="text-gray-600">
            Try adjusting your filters or search terms.
          </p>
        </div>
      )}
    </div>
  );
};

export default HospitalDirectory;
